import moment from "moment";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { IoIosAddCircleOutline } from "react-icons/io";
import {
  IoCloseCircleOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import { FiChevronDown, FiArrowLeft } from "react-icons/fi";
import DatePicker from "react-datepicker";
import { formatTime } from "../../utils";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import MyYuukke from "../../api/MyYuukke";
import BlockUi from "react-block-ui";
import { getUserSlots } from "../../redux/actions/calendarAction";
import { useDispatch } from "react-redux";
import "./WeeklyCalendar.css";

/* ═══════════════════════════════════════════════════════════
   TOGGLE SWITCH
═══════════════════════════════════════════════════════════ */
function Toggle({ on, onChange }) {
  return (
    <label className="wc-tog-wrap">
      <input type="checkbox" checked={on} onChange={onChange} />
      <span className="wc-tog-track" />
      <span className="wc-tog-thumb" />
    </label>
  );
}

/* ═══════════════════════════════════════════════════════════
   CUSTOM DATE PICKER INPUT
═══════════════════════════════════════════════════════════ */
const TimeInput = forwardRef(({ value, onClick }, ref) => (
  <input
    ref={ref}
    value={value}
    onClick={onClick}
    readOnly
    className="wc-time-input"
    placeholder="-- : --"
  />
));

/* ═══════════════════════════════════════════════════════════
   MINI CALENDAR
═══════════════════════════════════════════════════════════ */
const DOW    = { 0:"SUN",1:"MON",2:"TUE",3:"WED",4:"THU",5:"FRI",6:"SAT" };
const MNAMES = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];

// Half the tooltip's max-width (see .wc-tooltip in the CSS) — used to
// keep the popup from overflowing past the card's left/right edge.
const TOOLTIP_HALF_WIDTH = 112;

function MiniCalendar({ weeklyHours }) {
  const TODAY   = useMemo(() => new Date(), []);
  const [cur, setCur]   = useState(new Date(TODAY.getFullYear(), TODAY.getMonth(), 1));
  const [tip, setTip]   = useState(null);
  const [aKey, setAKey] = useState(0);
  const rootRef         = useRef(null);

  const y = cur.getFullYear(), m = cur.getMonth();
  const startDow  = (new Date(y,m,1).getDay()+6)%7;
  const totalDays = new Date(y,m+1,0).getDate();
  const prevTotal = new Date(y,m,0).getDate();

  const slotMap = useMemo(() => {
    const map = {};
    (weeklyHours||[]).forEach(d => { map[d.day]=d.timeSlots||[]; });
    return map;
  }, [weeklyHours]);

  const navMonth = dir => { setCur(new Date(y,m+dir,1)); setAKey(k=>k+1); setTip(null); };
  const goToday  = ()  => { setCur(new Date(TODAY.getFullYear(),TODAY.getMonth(),1)); setAKey(k=>k+1); setTip(null); };

  const cells = [];
  for (let i=startDow-1;i>=0;i--) cells.push({n:prevTotal-i,mine:false});
  for (let d=1;d<=totalDays;d++)  cells.push({n:d,mine:true});
  const rows = cells.length>35?42:35;
  while(cells.length<rows) cells.push({n:cells.length-startDow-totalDays+1,mine:false});

  // Tapping/clicking anywhere outside the calendar card dismisses the
  // tooltip — required on touch devices, which have no mouseleave.
  useEffect(() => {
    if (!tip) return;
    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setTip(null);
    };
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [tip]);

  const buildTip = (e, cell) => {
    if (!cell.mine) return null;
    const dt = new Date(y, m, cell.n), dn = DOW[dt.getDay()], slots = slotMap[dn] || [];
    if (!slots.length) return null;

    const r = e.currentTarget.getBoundingClientRect();
    const pr = rootRef.current.getBoundingClientRect();
    const rawLeft = r.left - pr.left + r.width / 2;
    // Clamp so the tooltip can never render partway off the card's
    // edge — this was effectively invisible/unusable on narrow mobile
    // widths before, since nothing kept it on-screen.
    const clampedLeft = Math.min(
      Math.max(rawLeft, TOOLTIP_HALF_WIDTH + 8),
      Math.max(pr.width - TOOLTIP_HALF_WIDTH - 8, TOOLTIP_HALF_WIDTH + 8)
    );
    return {
      dayName: dn,
      slots,
      top: r.top - pr.top,
      left: clampedLeft,
      caretOffset: rawLeft - clampedLeft, // how far the real cell is from the clamped tooltip center
    };
  };

  // Hover: desktop/mouse convenience — shows on enter, hides on leave.
  const onHover = (e, cell) => setTip(buildTip(e, cell) || null);

  // Tap/click: the actual mobile interaction. Tapping the same open
  // day again closes it (a toggle, standing in for "mouse leave"
  // which touch devices don't have).
  const onCellTap = (e, cell) => {
    if (!cell.mine) return;
    const dt = new Date(y, m, cell.n), dn = DOW[dt.getDay()];
    if (tip && tip.dayName === dn) {
      setTip(null);
    } else {
      setTip(buildTip(e, cell));
    }
  };

  const onCellKeyDown = (e, cell) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onCellTap(e, cell);
    } else if (e.key === "Escape") {
      setTip(null);
    }
  };

  return (
    <div className="wc-cal-card" ref={rootRef}>
      <div className="wc-cal-head">
        <div className="wc-cal-month-wrap">
          <span className="wc-cal-month">{MNAMES[m]} {y}</span>
        </div>
        <div className="wc-cal-controls">
          <button className="wc-cal-today-btn" onClick={goToday}>Today</button>
          <button className="wc-cal-nav" onClick={()=>navMonth(-1)}>‹</button>
          <button className="wc-cal-nav" onClick={()=>navMonth(1)}>›</button>
        </div>
      </div>

      {/* Hint text — hover has no touch equivalent, so mobile users
          need to be told tapping a day works. Hidden on desktop via CSS. */}
      <p className="wc-cal-hint mobile-only">Tap a highlighted day to see its hours</p>

      <div className="wc-cal-body">
        <div className="wc-cal-weekdays">
          {["Mo","Tu","We","Th","Fr","Sa","Su"].map(d=><div key={d} className="wc-cal-wd">{d}</div>)}
        </div>
        <div key={aKey} className="wc-cal-grid">
          {cells.map(({n,mine},idx)=>{
            if(!mine) return <div key={idx} className="wc-cal-cell other-month"><span className="wc-cal-dnum">{n}</span></div>;
            const dt=new Date(y,m,n),dn=DOW[dt.getDay()],slots=slotMap[dn]||[];
            const hasSlot=slots.length>0,isToday=dt.toDateString()===TODAY.toDateString(),isHov=tip?.dayName===dn&&hasSlot;
            return (
              <div key={idx}
                className={["wc-cal-cell",hasSlot?"has-slot":"",isToday?"is-today":"",isHov?"is-hovered":""].join(" ").trim()}
                style={{animationDelay:`${(idx%7)*.022}s`}}
                role={hasSlot ? "button" : undefined}
                tabIndex={hasSlot ? 0 : -1}
                aria-label={hasSlot ? `${dn}, ${slots.length} slot${slots.length>1?"s":""}` : undefined}
                onMouseEnter={e=>onHover(e,{n,mine})}
                onMouseLeave={()=>setTip(null)}
                onClick={e=>onCellTap(e,{n,mine})}
                onKeyDown={e=>onCellKeyDown(e,{n,mine})}>
                <span className="wc-cal-dnum">{n}</span>
                {hasSlot&&<div className="wc-slot-dots">
                  {Array.from({length:Math.min(slots.length,3)}).map((_,si)=>(
                    <span key={si} className="wc-slot-dot" style={{animationDelay:`${si*.22}s`}}/>
                  ))}
                  {slots.length>3&&<span className="wc-slot-dot-extra">+{slots.length-3}</span>}
                </div>}
              </div>
            );
          })}
        </div>
      </div>

      {tip&&(
        <div className="wc-tooltip" style={{top:tip.top-12,left:tip.left}}>
          <span className="wc-tooltip-caret" style={{left:`calc(50% + ${tip.caretOffset}px)`}}/>
          <div className="wc-tooltip-head">
            <span className="wc-tooltip-day">{tip.dayName}</span>
            <span className="wc-tooltip-count">{tip.slots.length} {tip.slots.length>1?"slots":"slot"}</span>
            <button
              type="button"
              className="wc-tooltip-close mobile-only"
              onClick={() => setTip(null)}
              aria-label="Close"
            >
              <IoCloseCircleOutline />
            </button>
          </div>
          {tip.slots.map((slot,si)=>(
            <div key={si} className="wc-tooltip-slot">
              <span className="wc-tooltip-dot" style={{animationDelay:`${si*.18}s`}}/>
              <span className="wc-tooltip-from">{moment(`2025-01-01T${slot.from}:00`).format("h:mm A")}</span>
              <span className="wc-tooltip-arrow">→</span>
              <span className="wc-tooltip-to">{moment(`2025-01-01T${slot.to}:00`).format("h:mm A")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DEFAULTS
═══════════════════════════════════════════════════════════ */
const makeDefault = () =>
  ["SUN","MON","TUE","WED","THU","FRI","SAT"].map(day=>({
    day,
    timeSlots:[{from:formatTime(new Date()),to:formatTime(new Date(),60)}],
  }));

const sharedDp = {
  showTimeSelect:     true,
  showTimeSelectOnly: true,
  timeCaption:        "Time",
  dateFormat:         "h:mm aa",
  portalId:           "root",
  popperPlacement:    "bottom-start",
  popperStrategy:     "fixed",
  popperModifiers: [
    { name: "offset",          options: { offset: [0, 6] } },
    { name: "preventOverflow", options: { rootBoundary: "viewport", padding: 8 } },
    { name: "flip",            options: { fallbackPlacements: ["top-start", "bottom-end"] } },
  ],
};

const EMPTY_AGENDA = { today: [], items: [], rangeLabel: "", range: "week", confirmedCount: 0, pendingCount: 0 };
const STATUS_LABELS = { CONFIRMED: "Confirmed", NEEDS_REVIEW: "Needs review", PENDING: "Pending", CANCELLED: "Cancelled" };
const statusLabel = (status) => STATUS_LABELS[(status || "").toUpperCase()] || (status || "Unknown");
const isReviewStatus = (status) => ["NEEDS_REVIEW", "PENDING"].includes((status || "").toUpperCase());

const normalizeAgenda = (data, fallbackRange) => ({
  today: Array.isArray(data?.today) ? data.today : [],
  items: Array.isArray(data?.items) ? data.items : [],
  rangeLabel: typeof data?.rangeLabel === "string" ? data.rangeLabel : "",
  range: data?.range || fallbackRange,
  confirmedCount: Number.isFinite(data?.confirmedCount) ? data.confirmedCount : 0,
  pendingCount: Number.isFinite(data?.pendingCount) ? data.pendingCount : 0,
});

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export const WeeklyCalendar = ({
  activeMinutes=30, makeAction, addSuccess, userSlots, from,
}) => {
  const maxTime  = new Date("1970-01-01T23:59:59");
  const minTime  = new Date("1970-01-01T00:00:00");
  const dispatch = useDispatch();

  const [isAddingAvailability, setIsAddingAvailability] = useState(false);

  const [state, setState] = useState({
    weeklyHours: userSlots?.length>0 ? userSlots : makeDefault(),
    makeChanges: false,
    loader: false,
  });

  const [viewRange, setViewRange]         = useState("week");
  const [rangeOffset, setRangeOffset]     = useState(0);
  const [retryKey, setRetryKey]           = useState(0);
  const [agenda, setAgenda]               = useState(EMPTY_AGENDA);
  const [agendaLoading, setAgendaLoading] = useState(true);
  const [agendaError, setAgendaError]     = useState(false);

  const durGap = from==="service" ? 60 : activeMinutes;

  useEffect(() => {
    let cancelled = false;
    setAgendaLoading(true); setAgendaError(false);
    
    const loadAgenda = async () => {
      try {
        if (typeof MyYuukke?.getAgenda !== "function") throw new Error("MyYuukke.getAgenda missing");
        
        const res = await MyYuukke.getAgenda({ range: viewRange, offset: rangeOffset });
        if (cancelled) return;
        setAgenda(normalizeAgenda(res?.data?.data, viewRange));
      } catch (err) {
        if (cancelled) return;
        setAgendaError(true); 
        setAgenda(EMPTY_AGENDA);
      } finally {
        if (!cancelled) setAgendaLoading(false);
      }
    };
    
    loadAgenda();
    return () => { cancelled = true; };
  }, [viewRange, rangeOffset, retryKey]);

  const modiFyTimeSlot = (type,index,t_i) => {
    const list = state.weeklyHours[index].timeSlots;
    if(type==="delete")      list.splice(t_i,1);
    else if(type==="remove") list.length=0;
    else {
      const ft = list.length ? list[list.length-1]?.to : "10:00";
      list.push({from:ft, to:moment(`2025-01-01T${ft}:00`).add(durGap,"minutes").locale("en").format("HH:mm")});
    }
    setState(p=>({...p,makeChanges:!p.makeChanges}));
  };

  const updateTimeSlots = (index,name,time,t_i) => {
    const minutes = formatTime(time);
    const overlap = state.weeklyHours[index].timeSlots.some((item,idx)=>{
      if(!item||idx===t_i) return false;
      const eF=moment(item.from,"HH:mm"),eT=moment(item.to,"HH:mm");
      const nF=moment(minutes,"HH:mm"),nT=moment(minutes,"HH:mm").add(1,"hour");
      return nF.isBefore(eT)&&nT.isAfter(eF);
    });
    if(overlap){toast.warning("Slot overlaps");return;}
    setState(prev=>{
      const wh=[...prev.weeklyHours], ts=[...wh[index].timeSlots];
      if(from==="service") ts[t_i]={...ts[t_i],[name]:formatTime(time),to:moment(formatTime(time),"HH:mm").add(durGap,"minutes").format("HH:mm")};
      else                 ts[t_i]={...ts[t_i],[name]:formatTime(time)};
      wh[index]={...wh[index],timeSlots:ts};
      return {...prev,weeklyHours:wh};
    });
  };

  const saveUserAvailability = () => {
    setState(p=>({...p,loader:true}));
    MyYuukke.saveUserAvailability({slots:state.weeklyHours})
      .then(()=>{
        toast.success("Calendar updated");
        setState(p=>({...p,loader:false}));
        dispatch(getUserSlots());
        addSuccess(4);
        setIsAddingAvailability(false);
      })
      .catch(()=>{
        toast.error("Error saving slots");
        setState(p=>({...p,loader:false}));
      });
  };

  const activeDays = state.weeklyHours.filter(d=>d.timeSlots.length>0);
  const activeCount = activeDays.length;
  
  const todayItems = agenda.today || [];
  const displayItems = agenda.items || [];

  const getRecurringString = () => {
    if (activeCount === 0) return "No recurring hours";
    const days = activeDays.map(d => d.day.charAt(0).toUpperCase() + d.day.slice(1,3).toLowerCase());
    let daysStr = days.join(', ');
    if (days.length === 2) daysStr = days.join(' and ');
    else if (days.length > 2) daysStr = days.slice(0, -1).join(', ') + ', and ' + days[days.length - 1];
    const timeStr = `${activeDays[0].timeSlots[0].from}–${activeDays[0].timeSlots[activeDays[0].timeSlots.length-1].to}`;
    return `${daysStr} · ${timeStr}`;
  };

  return (
    <BlockUi tag="div" message="Loading, please wait" style={{minHeight:"100px"}} className="wallLoadingPage" blocking={state.loader}>
      <div className="wc-root">

        {/* ── Mobile Top Bar (Only visible on small screens) ── */}
        <div className="wf-mobile-app-bar mobile-only">
          <FiArrowLeft className="wf-icon" onClick={() => {
            if (isAddingAvailability) setIsAddingAvailability(false);
            else if (makeAction) makeAction(7);
          }} />
          <h1 className="wf-mobile-title">Calendar</h1>
        </div>

        {/* ── Page Header (Desktop) ── */}
        <div className="wf-page-header desktop-only">
          <div className="wf-header-left">
            <p className="wf-eyebrow">Time commitments · Asia/Kolkata</p>
            <h1 className="wf-title">Calendar</h1>
            <p className="wf-subtitle">Appointments and booking requests, with the time context needed to act safely.</p>
          </div>
          
          {/* Action buttons aligned right */}
          <div className="wf-header-right">
            {!isAddingAvailability ? (
              <>
             
                <button className="wf-btn-outline" onClick={() => { setViewRange("week"); setRangeOffset(0); }}>Today</button>
                <button className="wf-btn-primary" onClick={() => setIsAddingAvailability(true)}>Add availability</button>
              </>
            ) : (
              <>
                <button className="wf-btn-outline" onClick={() => setIsAddingAvailability(false)}>
                  <FiArrowLeft style={{marginRight: '6px'}} /> Back
                </button>
                <button className="wf-btn-outline" onClick={() => setIsAddingAvailability(false)}>Cancel</button>
                <button className="wf-btn-primary" onClick={saveUserAvailability}>Save Slots</button>
              </>
            )}
          </div>
        </div>

        {/* ── Page Header Context (Mobile) ── */}
        <div className="wf-mobile-context mobile-only">
          <p className="wf-eyebrow">Time commitments · Asia/Kolkata</p>
          <h2 className="wf-title" style={{fontSize: '22px'}}>Calendar</h2>
          <p className="wf-subtitle" style={{marginBottom: '16px'}}>Appointments and booking requests, with the time context needed to act safely.</p>
          
          {/* Mobile Buttons Layout */}
          <div className="wf-header-actions" style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
            {!isAddingAvailability ? (
              <>
                <button className="wf-btn-outline" style={{flex: '1 1 auto'}} onClick={() => makeAction && makeAction(7)}>
                  <FiArrowLeft style={{marginRight: '6px'}} /> Back
                </button>
                <button className="wf-btn-outline" style={{flex: '1 1 auto'}} onClick={() => { setViewRange("week"); setRangeOffset(0); }}>Today</button>
                <button className="wf-btn-primary" style={{flex: '1 1 100%'}} onClick={() => setIsAddingAvailability(true)}>Add availability</button>
              </>
            ) : (
              <>
                <button className="wf-btn-outline" style={{flex: '1 1 auto'}} onClick={() => setIsAddingAvailability(false)}>
                  <FiArrowLeft style={{marginRight: '6px'}} /> Back
                </button>
                <button className="wf-btn-outline" style={{flex: '1 1 auto'}} onClick={() => setIsAddingAvailability(false)}>Cancel</button>
                <button className="wf-btn-primary" style={{flex: '1 1 100%'}} onClick={saveUserAvailability}>Save Slots</button>
              </>
            )}
          </div>
        </div>

        {isAddingAvailability ? (
          /* ═══════════════════════════════════════════════════════════
             VIEW: ADD AVAILABILITY (EDIT MODE)
          ═══════════════════════════════════════════════════════════ */
          <div className="wc-fade-in mt-3">
            <div className="wc-grid">
              {/* DEFAULT AVAILABILITY CONFIG */}
              <div className="wc-card">
                <div className="wc-card-head" style={{borderBottom: '1px solid var(--wc-border)', padding: '16px 20px'}}>
                  <span className="wc-card-title" style={{fontSize: '16px', fontWeight: 700}}>Default Availability</span>
                  <span className="wc-card-meta">{activeCount} days active</span>
                </div>
                <div className="wc-avail-list">
                  {state.weeklyHours.map((item,i)=>{
                    const isOn = item.timeSlots.length>0;
                    return (
                      <div key={i} className={`wc-day-block${isOn?"":" off"}`}>
                        <div className="wc-day-header-row">
                          <Toggle on={isOn} onChange={()=>modiFyTimeSlot(isOn?"remove":"add",i)}/>
                          <div className="wc-day-meta">
                            <div className="wc-day-name">{item.day}</div>
                            {isOn ? <span className="wc-slot-badge">{item.timeSlots.length} slots</span> : <span className="wc-unavail-txt">Unavailable</span>}
                          </div>
                          <div style={{flex:1}}/>
                          <button className="wc-btn-add-slot" onClick={()=>modiFyTimeSlot("add",i)}><IoIosAddCircleOutline className="wc-add-icon"/></button>
                        </div>
                        {item.timeSlots.map((time, t_i) => (
                          <div key={t_i} className="wc-slot-row">
                            <DatePicker {...sharedDp} selected={new Date(`2025-01-01T${time.from}:00`)} onChange={date=>updateTimeSlots(i,"from",date,t_i,time,item)} timeIntervals={activeMinutes} minTime={minTime} maxTime={maxTime} customInput={<TimeInput/>}/>
                            <span className="wc-slot-sep">-</span>
                            <div className="wc-slot-to">{moment(`2025-01-01T${time.from}:00`).locale("en").add(durGap,"minutes").format("h:mm A")}</div>
                            <button className="wc-btn-remove-slot" onClick={()=>modiFyTimeSlot("delete",i,t_i)}><IoCloseCircleOutline/></button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* MINI CALENDAR (Visible on Mobile now too) */}
              <div style={{display:"flex",flexDirection:"column",gap:"18px"}}>
                <MiniCalendar weeklyHours={state.weeklyHours}/>
              </div>
            </div>
          </div>
        ) : (
          /* ═══════════════════════════════════════════════════════════
             VIEW: READ-ONLY API DATA
          ═══════════════════════════════════════════════════════════ */
          <div className="wc-fade-in mt-3">
            {/* Top Toolbar (Desktop) */}
            <div className="wf-toolbar desktop-only">
              <div className="wf-date-picker-mock-full">
                <FiArrowLeft className="wf-icon" onClick={() => setRangeOffset(o=>o-1)} />
                <span className="wf-bold">{agendaLoading ? "..." : agenda.rangeLabel || moment().locale('en').format("D–[xx] MMMM YYYY")}</span>
                <div className="wf-divider"></div>
                <span className="wf-view-text">{viewRange === 'week' ? 'Week view' : 'Month view'}</span>
                <FiChevronDown className="wf-icon-right" style={{marginLeft: 'auto'}} />
              </div>
            </div>

            {/* Top Toolbar (Mobile) */}
            <div className="wf-mobile-controls mobile-only">
              <div className="wf-mobile-date-picker">
                <span>{agendaLoading ? "..." : agenda.rangeLabel || moment().locale('en').format("MMMM YYYY")}</span>
                <FiChevronDown className="wf-icon-right" />
              </div>
            </div>

            {/* Tabs (Available on BOTH Desktop and Mobile) */}
            <div className="wf-tabs-container">
              <div className="wf-tabs">
                <div className={`wf-tab ${viewRange === 'week' ? 'active' : ''}`} onClick={() => { setViewRange("week"); setRangeOffset(0); }}>Week</div>
                <div className={`wf-tab ${viewRange === 'month' ? 'active' : ''}`} onClick={() => { setViewRange("month"); setRangeOffset(0); }}>Month</div>
              </div>
            </div>

            {/* Main Split Content */}
            <div className="wf-grid mt-3">
              
              {/* LEFT / MAIN: Your Agenda (TODAY API DATA) */}
              <div className="wf-panel wf-agenda-panel">
                <div className="wf-panel-header">
                  <span className="wf-panel-date">{moment().locale('en').format('dddd, D MMMM')}</span>
                  <div className="wf-badge-circle primary desktop-only">1</div>
                </div>
                <div className="wf-panel-title-row">
                  <h2 className="wf-panel-title">Your agenda</h2>
                  <span className="wf-items-count desktop-only">{todayItems.length} items</span>
                </div>
                
                <div className="wf-agenda-list">
                  {agendaLoading && <div className="wf-empty-state">Loading schedule...</div>}
                  {!agendaLoading && agendaError && <div className="wf-empty-state">Failed to load agenda.</div>}
                  {!agendaLoading && !agendaError && todayItems.length === 0 && (
                     <div className="wf-empty-state">No meetings scheduled for today.</div>
                  )}
                  {!agendaLoading && !agendaError && todayItems.map((item, idx) => (
                    <div className="wf-agenda-item" key={item.bookingId || idx}>
                      <div className="wf-ai-time">{item.startTime || "—"}</div>
                      <div className="wf-ai-content">
                        <div className="wf-ai-title">{item.serviceName || "Booking"}</div>
                        <div className="wf-ai-sub">{item.clientName || "—"}{item.duration ? ` · ${item.duration}` : ''}</div>
                      </div>
                      <div className={`wf-ai-status ${isReviewStatus(item.status) ? 'review' : 'confirmed'}`}>
                        {statusLabel(item.status)}
                      </div>
                      <FiChevronDown className="wf-ai-chevron desktop-only" />
                    </div>
                  ))}
                </div>

                {/* Mobile Recurring Callout (Injected below agenda on mobile) */}
                <div className="wf-recurring-mobile mobile-only">
                  <IoInformationCircleOutline className="wf-recurring-icon" />
                  <div>
                    <div className="wf-recurring-title">Recurring availability</div>
                    <div className="wf-recurring-text">{getRecurringString()}</div>
                  </div>
                </div>
              </div>

              {/* RIGHT: Schedule Context (Offered Hours) - Hidden on Mobile */}
              <div className="wf-panel desktop-only">
                <div className="wf-panel-header">
                  <span className="wf-panel-eyebrow">Schedule context</span>
                  <div className="wf-badge-circle primary">2</div>
                </div>
                <div className="wf-panel-title-row" style={{marginBottom: '12px'}}>
                  <h2 className="wf-panel-title">Offered hours</h2>
                </div>
                <p className="wf-panel-desc">
                  The calendar shows your recurring windows so commitments do not look detached from availability.
                </p>

                <div className="wf-offered-list">
                  {activeDays.map((day, idx) => (
                    <div className="wf-offered-row" key={idx}>
                      <div className="wf-offered-day">{day.day.substring(0,3).charAt(0).toUpperCase() + day.day.substring(1,3).toLowerCase()}</div>
                      <div className="wf-offered-time">
                        {day.timeSlots[0].from}–{day.timeSlots[day.timeSlots.length-1].to}
                      </div>
                      <div className="wf-offered-tz">IST</div>
                    </div>
                  ))}
                  {activeCount === 0 && (
                     <div className="wf-offered-empty">No hours offered currently.</div>
                  )}
                </div>

                <div className="wf-callout">
                  <IoInformationCircleOutline className="wf-callout-icon" />
                  <div>
                    <div className="wf-callout-title">Pending requests stay separate</div>
                    <div className="wf-callout-text">A request becomes a commitment only after the source confirms it.</div>
                  </div>
                </div>
              </div>

            </div>

            {/* BOTTOM: This Week Table - Hidden on Mobile (WEEK/MONTH API DATA) */}
            <div className="wf-table-panel desktop-only">
              <div className="wf-table-header">
                <h2 className="wf-panel-title">This {viewRange}</h2>
                <div className="wf-status-pill">{agenda.confirmedCount || 0} confirmed · {agenda.pendingCount || 0} pending</div>
              </div>
              <table className="wf-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Time</th>
                    <th>Record</th>
                    <th>Client</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {agendaLoading && <tr><td colSpan="5" className="wf-empty-state" style={{padding: "20px 0"}}>Loading...</td></tr>}
                  {!agendaLoading && !agendaError && displayItems.length === 0 && (
                    <tr><td colSpan="5" className="wf-empty-state" style={{padding: "20px 0"}}>No bookings this {viewRange}.</td></tr>
                  )}
                  {!agendaLoading && !agendaError && displayItems.map((item, idx) => (
                    <tr key={item.bookingId || idx}>
                      <td className="wf-td-day">{item.dayLabel || "—"}</td>
                      <td>{item.startTime || "—"}</td>
                      <td>{item.serviceName || "Booking"}</td>
                      <td className="wf-td-client">{item.clientName || "—"}</td>
                      <td>
                        <div className={`wf-ai-status ${isReviewStatus(item.status) ? 'review' : 'confirmed'}`} style={{display: 'inline-flex'}}>
                          {statusLabel(item.status)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        )}

      </div>
    </BlockUi>
  );
};

export default withTranslation()(WeeklyCalendar);